import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { SlideOver } from '@/components/SlideOver.js';
import { TicketPaymentsPanel } from '@/components/payments/TicketPaymentsPanel.js';
import { ProcessReturnModal } from '@/components/sales/ProcessReturnModal.js';
import { PrintButton } from '@/components/printing/PrintButton.js';
import { usePrintNodeConfigured } from '@/hooks/usePrintNodeConfigured.js';
import { getSale } from '@/lib/api/sales.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import {
  paymentMethodEmoji,
  paymentMethodLabel,
  staffShortName,
} from '@/lib/payment-display.js';

function SaleStatusBadge({
  balanceDue,
  hasReturn,
  returnTotal,
  total,
}: {
  balanceDue: number;
  hasReturn?: boolean;
  returnTotal?: string | null;
  total: number;
}) {
  if (hasReturn && Number(returnTotal ?? 0) >= total - 0.001) {
    return (
      <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-bold text-slate-700">
        Returned
      </span>
    );
  }
  if (hasReturn && Number(returnTotal ?? 0) > 0) {
    return (
      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
        Partial Return
      </span>
    );
  }
  if (balanceDue > 0.001) {
    return (
      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
        Balance Due
      </span>
    );
  }
  return (
    <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700">
      Paid
    </span>
  );
}

export function SaleDetailSlideOver({
  open,
  saleId,
  onClose,
}: {
  open: boolean;
  saleId: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [returnOpen, setReturnOpen] = useState(false);
  const { hasPrintNode } = usePrintNodeConfigured('receipt');

  const saleQuery = useQuery({
    queryKey: ['sales', saleId],
    queryFn: () => getSale(saleId!),
    enabled: open && Boolean(saleId),
  });

  const sale = saleQuery.data;
  const saleNumber = sale?.saleNumber ?? sale?.ticketNumber ?? 'Sale';
  const tax = sale?.taxTotal ?? sale?.taxAmount ?? 0;
  const paid = sale?.paidTotal ?? sale?.amountPaid ?? 0;
  const returns = sale?.returns ?? [];

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['sales', saleId] });
    void queryClient.invalidateQueries({ queryKey: ['sales'] });
    void queryClient.invalidateQueries({ queryKey: ['payments', 'ticket', saleId] });
  }

  return (
    <>
      <SlideOver
        title={saleNumber}
        open={open}
        onClose={onClose}
        panelClassName="max-w-2xl"
      >
        {saleQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading sale…</p>
        ) : saleQuery.isError || !sale ? (
          <p className="text-sm text-red-600">Unable to load sale details.</p>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm text-slate-500">{formatDateTime(sale.createdAt)}</p>
                <p className="mt-1 text-base font-semibold text-slate-950">
                  {sale.customerName ?? 'Walk-in'}
                  {sale.customerPhone ? ` · ${sale.customerPhone}` : ''}
                </p>
              </div>
              <SaleStatusBadge
                balanceDue={Number(sale.balanceDue)}
                hasReturn={sale.hasReturn || returns.length > 0}
                returnTotal={sale.returnTotal}
                total={Number(sale.total)}
              />
            </div>

            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Items Sold</h3>
              <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200">
                {(sale.lineItems ?? [])
                  .filter((line) => line.type !== 'discount')
                  .map((line) => (
                    <li key={line.id} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-950">{line.description}</p>
                        <p className="text-xs text-slate-500">
                          Qty {line.quantity} · {formatCurrency(line.unitPrice)}
                        </p>
                      </div>
                      <span className="shrink-0 font-semibold">{formatCurrency(line.lineTotal)}</span>
                    </li>
                  ))}
              </ul>
            </section>

            <section className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-semibold">{formatCurrency(sale.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tax</span>
                <span className="font-semibold">{formatCurrency(tax)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
                <span>Total</span>
                <span>{formatCurrency(sale.total)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Paid</span>
                <span>{formatCurrency(paid)}</span>
              </div>
            </section>

            <TicketPaymentsPanel
              ticketId={sale.id}
              ticketNumber={saleNumber}
              customerName={sale.customerName ?? undefined}
              paidTotal={paid}
              balanceDue={sale.balanceDue}
              ticketTotal={sale.total}
              initialPayments={sale.payments}
              onChanged={refresh}
            />

            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Returns</h3>
              {returns.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No returns on this sale</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {returns.map((ret) => {
                    const items = ret.returnItems ?? ret.items ?? [];
                    return (
                      <li
                        key={ret.id}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm"
                      >
                        <div className="flex justify-between font-semibold">
                          <span>{ret.returnNumber}</span>
                          <span>{formatCurrency(ret.refundAmount)}</span>
                        </div>
                        <p className="mt-1 text-slate-500">
                          {paymentMethodEmoji(ret.refundMethod)} {paymentMethodLabel(ret.refundMethod)}
                          {' · '}
                          {formatDateTime(ret.createdAt)}
                          {ret.processedBy ? ` · ${staffShortName(ret.processedBy)}` : ''}
                        </p>
                        {items.length > 0 ? (
                          <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
                            {items.map((item, idx) => (
                              <li key={`${ret.id}-${item.lineItemId}-${idx}`}>
                                {item.itemName ?? item.description ?? 'Item'} ×{item.qty}
                                {item.returnToStock ? ' (restocked)' : ' (not restocked)'}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="space-y-1 text-sm text-slate-600">
              {sale.createdBy ? (
                <p>Recorded by: {staffShortName(sale.createdBy)}</p>
              ) : null}
              {sale.storeName ? <p>Store: {sale.storeName}</p> : null}
            </section>

            <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row">
              <PrintButton
                type="receipt"
                ticketId={sale.id}
                hasPrintNode={hasPrintNode}
                label="Print Receipt"
                className="w-full justify-center sm:w-auto"
              />
              <button
                type="button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 sm:w-auto"
                onClick={() => setReturnOpen(true)}
              >
                <RotateCcw className="h-4 w-4" />
                Process Return
              </button>
            </div>
          </div>
        )}
      </SlideOver>

      <ProcessReturnModal
        open={returnOpen}
        sale={sale ?? null}
        onClose={() => setReturnOpen(false)}
        onSuccess={() => refresh()}
      />
    </>
  );
}
